import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const MerchContext = createContext();

export const useMerch = () => {
    return useContext(MerchContext);
};

export const MerchProvider = ({ children }) => {
    const { user } = useAuth();
    const [cart, setCart] = useState([]);
    const [isCartOpen, setIsCartOpen] = useState(false);

    // Load cart from local storage on mount
    useEffect(() => {
        const savedCart = localStorage.getItem('solis_merch_cart');
        if (savedCart) {
            try {
                setCart(JSON.parse(savedCart));
            } catch (e) {
                console.error('Failed to parse cart', e);
            }
        }
    }, []);

    // Save cart to local storage whenever it changes
    useEffect(() => {
        localStorage.setItem('solis_merch_cart', JSON.stringify(cart));
    }, [cart]);

    const addToCart = (product, variant, quantity) => {
        setCart(prev => {
            const existingItemIndex = prev.findIndex(item => item.variant_id === variant.id);
            if (existingItemIndex > -1) {
                const newCart = [...prev];
                newCart[existingItemIndex].quantity += quantity;
                return newCart;
            }
            return [...prev, {
                product_id: product.id,
                variant_id: variant.id, // This is the Sync Variant ID
                catalog_variant_id: variant.variant_id, // Map Printful Catalog ID
                name: product.name,
                variant_name: variant.name,
                retail_price: variant.retail_price,
                thumbnail_url: product.thumbnail_url, // or variant image
                quantity,
                files: variant.files // store print files ref if needed, usually just ID
            }];
        });
        setIsCartOpen(true);
    };

    const removeFromCart = (variantId) => {
        setCart(prev => prev.filter(item => item.variant_id !== variantId));
    };

    const updateQuantity = (variantId, quantity) => {
        if (quantity < 1) return removeFromCart(variantId);
        setCart(prev => prev.map(item =>
            item.variant_id === variantId ? { ...item, quantity } : item
        ));
    };

    const clearCart = () => {
        setCart([]);
        localStorage.removeItem('solis_merch_cart');
    };

    const cartTotal = cart.reduce((sum, item) => sum + (parseFloat(item.retail_price) * item.quantity), 0);
    const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <MerchContext.Provider value={{
            cart,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            isCartOpen,
            setIsCartOpen,
            cartTotal,
            cartCount
        }}>
            {children}
        </MerchContext.Provider>
    );
};
